import { Group, Stack } from '@mantine/core';

import { ButtonMantine } from '../../../../../Buttons';
import { Card } from '../../../../../DataDisplay/Card/Card';
import { useId } from '../../../../../Hooks/state-management';
import { Text } from '../../../../../Typography';
import { ConditionalWrapper } from '../../../../../Utils/ConditionalWrapper';
import { DraggableWrapper } from '../../../dragAndDrop/DraggableWrapper';
import { DroppableWrapper } from '../../../dragAndDrop/DroppableWrapper';
import { AdditionalFieldIOption_Internal } from '../AdditionalFields.types';
import { CustomInputProps } from '../customInputProps.types';
import { FieldLabel } from './FieldLabel';

type HandleUpdateOrderFn = (from: number, to: number) => void;

type SelectionOptionsProps = {
  options: AdditionalFieldIOption_Internal['options'];
  onAddOption: () => void;
  onRemoveOption: (index: number) => void;
  getOptionsProps: (index: number) => CustomInputProps;
  isOrderable?: boolean;
  isDisabled?: boolean;
  handleUpdateOrder: HandleUpdateOrderFn;
};

interface DraggableFieldLabel
  extends Pick<SelectionOptionsProps, 'isOrderable' | 'onRemoveOption' | 'isDisabled'> {
  index: number;
  inputProps: CustomInputProps;
}
const DraggableFieldLabel = ({
  isOrderable,
  index,
  onRemoveOption,
  inputProps,
  isDisabled,
}: DraggableFieldLabel) => {
  return (
    <ConditionalWrapper
      condition={!!isOrderable}
      wrapper={(children) => (
        <DraggableWrapper key={index} index={index} handleProps={{ isDraggable: !isDisabled }}>
          {children}
        </DraggableWrapper>
      )}
    >
      <Stack style={{ flexGrow: 1 }}>
        <FieldLabel
          placeholder="Enter selection option"
          index={index}
          onRemove={onRemoveOption}
          isInputDisabled={isDisabled}
          showRemoveButton={!isDisabled}
          {...inputProps}
        />
      </Stack>
    </ConditionalWrapper>
  );
};

const OptionsDroppableWrapper = ({
  isOrderable = true,
  children,
  handleUpdateOrder,
}: {
  isOrderable?: boolean;
  children?: React.ReactNode | React.ReactNode[];
  handleUpdateOrder: HandleUpdateOrderFn;
}) => {
  const id = useId();
  return (
    <ConditionalWrapper
      condition={!!isOrderable}
      wrapper={() => (
        <DroppableWrapper
          handleUpdateOrder={handleUpdateOrder}
          droppableId={`select-additionalFields${id}`}
        >
          {children}
        </DroppableWrapper>
      )}
    >
      {children}
    </ConditionalWrapper>
  );
};

const SelectionOptions = ({
  options,
  onAddOption,
  onRemoveOption,
  getOptionsProps,
  handleUpdateOrder,
  isOrderable = true,
  isDisabled = false,
}: SelectionOptionsProps) => {
  return (
    <Card
      shadow="none"
      radius="sm"
      style={(theme) => ({ border: `1px solid ${theme.colors.gray[1]}` })}
    >
      <Card.Section bg="gray.0">
        <Group justify="space-between" p="sm">
          <Text fz="14" fw="500">
            Selection Options
          </Text>
          <ButtonMantine size="xs" onClick={onAddOption} disabled={isDisabled}>
            Add Selection Option
          </ButtonMantine>
        </Group>
      </Card.Section>
      {Boolean(options?.length) && (
        <OptionsDroppableWrapper isOrderable={isOrderable} handleUpdateOrder={handleUpdateOrder}>
          <Stack mt="sm">
            {options?.map((option, index) => (
              <DraggableFieldLabel
                isOrderable={isOrderable}
                key={option.key}
                index={index}
                onRemoveOption={onRemoveOption}
                inputProps={getOptionsProps(index)}
                isDisabled={isDisabled}
              />
            ))}
          </Stack>
        </OptionsDroppableWrapper>
      )}
    </Card>
  );
};

export { SelectionOptions };
